// src/components/lesson/ConceptExampleTemplate.jsx

import React from "react";
import { useNavigate } from "react-router-dom";

import LessonShell from "./LessonShell";
import LessonMedia from "./LessonMedia";

export default function ConceptExampleTemplate({
  /*
   * Existing production content
   */
  title = "",
  storyParagraphs = [],

  imageSrc = "",
  imageAlt = "Real example illustration",

  /*
   * Navigation
   */
  nextLabel = "NEXT",
  nextTo,
  playUrl,
  onNext,

  backTo,
  backLabel = "Back",
  onBack,

  /*
   * Lesson / concept position
   */
  conceptNumber = 1,
  conceptTotal = 1,

  progressStep = 1,
  progressTotal = 1,

  /*
   * Optional future media support.
   *
   * We are not connecting these to
   * Sanity yet.
   */
  videoSrc = "",
  videoPoster = "",

  animationSrc = "",

  mediaType = "",
  mediaLabel = "",

  /*
   * Optional future example metadata.
   *
   * Useful when we eventually have
   * characters such as Thabo, Naledi,
   * Thandi, etc.
   */
  characterName = "",
  takeaway = "",

  /*
   * Kept for compatibility with the
   * shared renderer props.
   */
  conceptIcon,
}) {
  const navigate = useNavigate();

  const handleNext = () => {
    if (onNext) {
      onNext();
      return;
    }

    if (!nextTo) {
      return;
    }

    if (
      typeof playUrl === "function"
    ) {
      navigate(
        playUrl(nextTo)
      );

      return;
    }

    navigate(nextTo);
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }

    if (backTo) {
      if (
        typeof playUrl === "function"
      ) {
        navigate(
          playUrl(backTo)
        );

        return;
      }

      navigate(backTo);
      return;
    }

    navigate(-1);
  };

  /*
   * MEDIA TYPE
   *
   * Existing Sanity illustration
   * remains the normal fallback.
   */
  const resolvedMediaType =
    mediaType ||
    (videoSrc
      ? "video"
      : animationSrc
      ? "animation"
      : "image");

  const resolvedMediaSrc =
    resolvedMediaType ===
    "video"
      ? videoSrc
      : resolvedMediaType ===
        "animation"
      ? animationSrc
      : imageSrc;

  const hasMedia =
    !!resolvedMediaSrc;

  const sectionLabel =
    conceptNumber
      ? `SECTION ${conceptNumber}: REAL EXAMPLE`
      : "REAL EXAMPLE";

  return (
    <LessonShell
      progressStep={
        progressStep
      }
      progressTotal={
        progressTotal
      }

      sectionLabel={
        sectionLabel
      }

      showBack
      backLabel={
        backLabel
      }
      onBack={
        handleBack
      }

      showNext
      nextLabel={
        nextLabel
      }
      onNext={
        handleNext
      }
    >
      {/*
       * EXAMPLE TITLE
       */}
      {!!title && (
        <h1
          className="
            text-[27px]
            font-extrabold
            leading-[1.12]
            tracking-[-0.025em]
            text-[#222222]
            sm:text-[30px]
          "
        >
          {title}
        </h1>
      )}

      {/*
       * OPTIONAL CHARACTER LABEL
       *
       * This won't render until we later
       * supply a characterName.
       */}
      {!!characterName && (
        <div className="mt-4">
          <span
            className="
              inline-flex
              items-center
              rounded-full
              bg-[#FFF0E7]
              px-3
              py-1.5
              text-[11px]
              font-bold
              uppercase
              tracking-[0.08em]
              text-[#FF7A3D]
            "
          >
            {characterName}
          </span>
        </div>
      )}

      {/*
       * STORY CARD
       *
       * Real Examples are visually
       * separated from the Learning Idea
       * page so the learner understands
       * that this is now a practical
       * business story/context.
       */}
      {storyParagraphs.length >
        0 && (
        <section
          className={[
            "rounded-[24px]",
            "border border-[#E8E8E8]",
            "bg-[#FAFAFA]",
            "px-5 py-5",

            title ||
            characterName
              ? "mt-5"
              : "",
          ].join(" ")}
        >
          <div className="space-y-4">
            {storyParagraphs.map(
              (
                paragraph,
                index
              ) => (
                <p
                  key={`${index}-${paragraph}`}
                  className="
                    text-[15px]
                    font-medium
                    leading-[1.65]
                    text-[#414141]
                  "
                >
                  {paragraph}
                </p>
              )
            )}
          </div>
        </section>
      )}

      {/*
       * CHARACTER / EXAMPLE MEDIA
       *
       * Current:
       * Sanity illustration
       *
       * Later:
       * character image
       * short example video
       * looping educational animation
       */}
      {hasMedia && (
        <section className="mt-6">
          <LessonMedia
            type={
              resolvedMediaType
            }
            src={
              resolvedMediaSrc
            }
            poster={
              videoPoster
            }
            alt={
              imageAlt
            }
            label={
              mediaLabel ||
              title ||
              "Real example media"
            }

            controls={
              resolvedMediaType ===
              "video"
            }

            autoPlay={
              resolvedMediaType ===
              "animation"
            }

            loop={
              resolvedMediaType ===
              "animation"
            }

            muted={
              resolvedMediaType ===
              "animation"
            }
          />
        </section>
      )}

      {/*
       * OPTIONAL EXAMPLE CONCLUSION
       *
       * We can connect this later if the
       * new lesson content needs a clear
       * final explanation underneath the
       * character/story.
       */}
      {!!takeaway && (
        <section
          className="
            mt-6
            rounded-[20px]
            border
            border-[#BCEAFB]
            bg-[#EFFAFF]
            px-5
            py-4
          "
        >
          <p
            className="
              text-[11px]
              font-extrabold
              uppercase
              tracking-[0.1em]
              text-[#38B6E8]
            "
          >
            What this shows
          </p>

          <p
            className="
              mt-2
              text-[14px]
              font-semibold
              leading-[1.55]
              text-[#292929]
            "
          >
            {takeaway}
          </p>
        </section>
      )}
    </LessonShell>
  );
}